import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Newspaper, Mail, BookmarkPlus, Check, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WizardSteps from "@/components/WizardSteps";
import { api, formatApiError } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";

export default function DoneStep() {
  const [params] = useSearchParams();
  const bookingId = params.get("b");
  const isMock = params.get("mock") === "1";
  const { draft } = useBooking();
  const guestEmail = draft?.advocate?.email || "";
  const [booking, setBooking] = useState(null);
  const [tplName, setTplName] = useState("");
  const [tplSaved, setTplSaved] = useState(false);
  const [tplBusy, setTplBusy] = useState(false);
  const [tplErr, setTplErr] = useState("");

  useEffect(() => {
    if (!bookingId) return;
    api.get(`/bookings/${bookingId}`, { params: guestEmail ? { email: guestEmail } : {} })
      .then(({ data }) => {
        setBooking(data);
        const guess = `${data.paper_name || "Template"} · ${(data.advocate?.name || "").split(" ").slice(-1)[0] || "notice"}`.slice(0, 60);
        setTplName(guess);
      })
      .catch((err) => console.warn("[DoneStep] booking fetch failed:", err));
  }, [bookingId, guestEmail]);

  const saveTemplate = async () => {
    setTplErr("");
    if (!tplName.trim()) { setTplErr("Please give your template a name"); return; }
    setTplBusy(true);
    try {
      await api.post(`/templates/from-booking/${bookingId}`, { name: tplName.trim() });
      setTplSaved(true);
    } catch (e) { setTplErr(formatApiError(e)); }
    finally { setTplBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 w-full">
        <WizardSteps current="done" />
        <div className="bg-white border border-black/15 rounded-sm p-7 sm:p-9 lg:p-12 text-center" data-testid="booking-done-card">
          <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-5">
            <CheckCircle2 size={28} className="text-emerald-600"/>
          </div>
          <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Confirmed</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-black mt-2 font-bold tracking-tight">Your notice is booked.</h1>
          <p className="text-gray-600 mt-3 max-w-xl mx-auto text-sm sm:text-base">Thank you. A confirmation email with the ad artwork PDF and GST invoice PDF has been sent. Our copy desk will reach out if any clarification is needed.</p>

          {isMock && (
            <div className="mt-6 inline-block px-4 py-2 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-bold tracking-overline uppercase rounded-sm">
              ⚠ MOCKED PAYMENT — Razorpay keys not configured
            </div>
          )}

          {booking && (
            <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left max-w-xl mx-auto bg-gray-50 p-5 border-l-4 border-black">
              <KV k="Booking ID" v={booking.id.slice(0, 8).toUpperCase()} />
              <KV k="Newspaper" v={booking.paper_name} />
              <KV k="Publish Date" v={booking.publish_date} />
              <KV k="Total Paid" v={`₹${Number(booking.total_amount).toFixed(2)}`} />
              <KV k="Payment" v={booking.payment_status} />
              <KV k="Status" v={booking.status} />
            </div>
          )}

          {/* SAVE AS TEMPLATE (only for logged-in users — templates endpoint requires auth) */}
          {booking && !tplSaved && booking.user_id && (
            <div className="mt-9 max-w-xl mx-auto text-left bg-white border border-black/15 rounded-sm p-5" data-testid="save-template-card">
              <div className="flex items-start gap-3">
                <BookmarkPlus size={20} className="text-black mt-1"/>
                <div className="flex-1">
                  <div className="text-[10px] tracking-overline uppercase text-gray-500 font-bold">Save as Template</div>
                  <h3 className="font-display text-lg text-black mt-1 font-bold">Re-use this notice next time in one tap.</h3>
                  <p className="text-xs text-gray-600 mt-1">Saves the paper, language, ad text, advocate name &amp; address. Letterhead is uploaded fresh each booking.</p>
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={tplName}
                      onChange={(e) => setTplName(e.target.value)}
                      placeholder="Template name (e.g. Property Purchase – Lokmat)"
                      className="flex-1 px-3 py-2 border border-black/20 focus:border-black focus:ring-1 focus:ring-black outline-none rounded-sm bg-white text-sm"
                      data-testid="template-name-input"
                    />
                    <button
                      onClick={saveTemplate}
                      disabled={tplBusy}
                      className="px-4 py-2 bg-black text-white rounded-sm hover:bg-gray-800 transition text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="save-template-btn"
                    >
                      {tplBusy ? <Loader2 size={14} className="animate-spin"/> : <BookmarkPlus size={14}/>}
                      Save template
                    </button>
                  </div>
                  {tplErr && <div className="mt-2 text-xs text-red-600" data-testid="template-error">{tplErr}</div>}
                </div>
              </div>
            </div>
          )}
          {tplSaved && (
            <div className="mt-9 max-w-xl mx-auto bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm p-4 text-sm flex items-center justify-center gap-2" data-testid="template-saved-ack">
              <Check size={16}/> Template saved. You&apos;ll see it on the next booking.
            </div>
          )}

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/dashboard" className="px-5 py-3 bg-black text-white hover:bg-gray-800 rounded-sm transition text-sm inline-flex items-center gap-2" data-testid="done-dashboard">
              <Newspaper size={16}/> Track my booking
            </Link>
            <Link to="/" className="px-5 py-3 border border-black/15 rounded-sm hover:border-black text-sm inline-flex items-center gap-2" data-testid="done-home">
              <Mail size={16}/> Back home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">{k}</div>
      <div className="text-black font-medium">{v}</div>
    </div>
  );
}
